import { motion } from "framer-motion";
import {
  BadgeCheck,
  CarFront,
  Clock3,
  Luggage,
  MapPinned,
  ShieldCheck,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const COPY = {
  en: {
    eyebrow: "Why BlackRide",
    title: "Designed for guests who care about how the arrival feels, not only how the ride is booked.",
    cards: [
      {
        icon: ShieldCheck,
        title: "TGA-approved premium service",
        desc: "Built for travelers who want a higher trust standard for airport, city, and intercity movement in Saudi Arabia.",
      },
      {
        icon: Clock3,
        title: "24/7 coordination",
        desc: "Useful for early-morning arrivals, late-night landings, and itineraries that cannot afford guesswork.",
      },
      {
        icon: BadgeCheck,
        title: "Meet-and-greet included",
        desc: "A stronger first handoff for international guests, families, and Umrah travelers after a long flight.",
      },
      {
        icon: CarFront,
        title: "Owned premium fleet",
        desc: "Cleaner control over vehicle standard, passenger comfort, and premium route delivery.",
      },
      {
        icon: Luggage,
        title: "Better fit for luggage-heavy arrivals",
        desc: "Vehicle guidance shaped around families, elders, children, and airport baggage realities.",
      },
      {
        icon: MapPinned,
        title: "Jeddah, Makkah, and Madinah expertise",
        desc: "Especially relevant when the first important route is Jeddah Airport to Makkah.",
      },
    ],
  },
  ar: {
    eyebrow: "لماذا بلاك رايد",
    title: "مصممة للضيوف الذين يهتمون بإحساس الوصول، وليس فقط بطريقة حجز الرحلة.",
    cards: [
      {
        icon: ShieldCheck,
        title: "خدمة راقية معتمدة من هيئة النقل",
        desc: "مناسبة للمسافرين الباحثين عن مستوى ثقة أعلى في التنقل من المطار وداخل المدن وبينها في السعودية.",
      },
      {
        icon: Clock3,
        title: "تنسيق 24/7",
        desc: "مثالية للوصولات المبكرة والمتأخرة والبرامج التي لا تحتمل العشوائية.",
      },
      {
        icon: BadgeCheck,
        title: "الاستقبال متضمن",
        desc: "بداية أقوى للضيوف الدوليين والعائلات وضيوف العمرة بعد رحلة طيران طويلة.",
      },
      {
        icon: CarFront,
        title: "أسطول مملوك وفاخر",
        desc: "تحكم أوضح في مستوى المركبة وراحة الركاب وجودة تنفيذ المسار.",
      },
      {
        icon: Luggage,
        title: "أنسب للوصولات كثيرة الأمتعة",
        desc: "ترشيح المركبة مبني على العائلات وكبار السن والأطفال وواقع أمتعة المطار.",
      },
      {
        icon: MapPinned,
        title: "خبرة جدة ومكة والمدينة",
        desc: "مهم جداً عندما يكون المسار الأهم هو مطار جدة إلى مكة.",
      },
    ],
  },
};

export default function Trust() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;

  return (
    <section id="trust" data-testid="trust-section" className="bg-[#050505] py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="mb-14 text-center"
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-[#C7A239]">{copy.eyebrow}</p>
          <h2 className="mx-auto max-w-5xl font-['Playfair_Display'] text-3xl font-semibold leading-tight text-white md:text-4xl lg:text-5xl">
            {copy.title}
          </h2>
        </motion.div>

        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {copy.cards.map((card, index) => (
            (() => {
              const Icon = card.icon;

              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: index * 0.05 }}
                  className="group rounded-[28px] border border-white/8 bg-[#111] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#C7A239]/25 hover:bg-[#151515]"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C7A239]/10 transition-colors group-hover:bg-[#C7A239]/16">
                    <Icon className="h-5 w-5 text-[#C7A239]" />
                  </div>
                  <h3 className="text-xl font-medium text-white">{card.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#a79f93]">{card.desc}</p>
                </motion.div>
              );
            })()
          ))}
        </div>
      </div>
    </section>
  );
}
